import { Fallback, Image, Root } from '@radix-ui/react-avatar';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export type AvatarProps = ComponentPropsWithoutRef<typeof Root>;
export type AvatarImageProps = ComponentPropsWithoutRef<typeof Image>;
export type AvatarFallbackProps = ComponentPropsWithoutRef<typeof Fallback>;

/**
 * Radix mounts the image only once it has actually loaded, so a presigned media
 * URL that expires or 403s degrades to the fallback initials instead of the
 * browser's broken-image glyph. That matters here: every member photo in this
 * portal is behind a per-request presigned GET (N12).
 */
export const Avatar = forwardRef<ElementRef<typeof Root>, AvatarProps>(function Avatar(
  { className, ...props },
  ref,
) {
  return (
    <Root
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted',
        className,
      )}
      {...props}
    />
  );
});

export const AvatarImage = forwardRef<ElementRef<typeof Image>, AvatarImageProps>(
  function AvatarImage({ className, ...props }, ref) {
    return <Image ref={ref} className={cn('h-full w-full object-cover', className)} {...props} />;
  },
);

export const AvatarFallback = forwardRef<ElementRef<typeof Fallback>, AvatarFallbackProps>(
  function AvatarFallback({ className, ...props }, ref) {
    return (
      <Fallback
        ref={ref}
        className={cn(
          'flex h-full w-full items-center justify-center bg-secondary text-label text-secondary-foreground',
          className,
        )}
        {...props}
      />
    );
  },
);
